import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { useRootStore } from 'store';
import { Card, Row } from 'react-bootstrap';
import {
  CellFloatCustom,
  CellLeft,
  FlexRow,
  FormFilterCard,
  TableInMem,
  YupStringReq,
  exportXlsModal,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import gql from 'graphql-tag';
import { SelectContributionYear } from '../components/SelectContributionYear';
import { formatFloat } from 'shared-pages/utils/helpers';

export const gqlGetYearlyContributionsPerContributor = gql`
  query getYearlyContributionsPerContributor($year: Int) {
    getYearlyContributionsPerContributor(year: $year) {
      data {
        contributorId
        contributorName
        vatNumber
        qty1
        qty2
        price
      }
      recordCount
    }
  }
`;

/* ----------- Page ------------------ */
export const YearlyContributorsContributionsPage = () => {
  const { appStore } = useRootStore();
  const client = useApolloClient();
  const { t } = useTranslation();
  /* ----------------- Route State -------------------------- */
  const location = useLocation<any>();
  const history = useHistory();
  // Στο state του routing αποθηκεύω:
  //    1. την τρέχουσα σελίδα του table pagination (pageIndex)
  //  & 2. τα values των φίλτρων (filterValues)

  /// Αρχικές Τιμές Στα Φίλτρα
  useEffect(() => {
    const values = location?.state?.filterValues ?? {};
    if (!values.year) {
      values.year = new Date().getFullYear();
      // Ενημέρωσε το State του Route
      history.replace(location.pathname, {
        ...location.state,
        filterValues: values,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [queryParams, setQueryParams] = useState({
    year: parseInt(location?.state?.filterValues.year) ?? 2030,
    contributorId: location?.state?.filterValues.contributorId ?? 999,
  });

  const [footerValues, setFooterValues] = useState({
    qty1: 0,
    qty2: 0,
    price: 0,
  });

  const [getData, { loading, data: dataRaw }] = useLazyQuery(
    gqlGetYearlyContributionsPerContributor,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        // Υπολόγισε αθροίσματα
        if (data.getYearlyContributionsPerContributor?.data) {
          //console.log('$$$ onCompleted', data.getYearlyContributionsPerContributor);
          let qty1 = 0;
          let qty2 = 0;
          let price = 0;
          for (
            let index = 0;
            index < data.getYearlyContributionsPerContributor?.data.length;
            index++
          ) {
            const values = data.getYearlyContributionsPerContributor?.data[index];
            qty1 += values.qty1;
            qty2 += values.qty2;
            price += values.price;
          }
          setFooterValues({ qty1, qty2, price });
        }
      },
    },
  );
  const data = dataRaw?.getYearlyContributionsPerContributor?.data;
  const recordCount = dataRaw?.getYearlyContributionsPerContributor?.recordCount;

  const filtersChanged = useCallback(
    (values) => {
      console.log('filtersChanged', values);

      // Σύνθεσε τα vars του graphql
      const newQueryParams = {
        ...queryParams,
        year: parseInt(values.year),
      };
      setQueryParams(newQueryParams); // αποθήκευσε τα vars
      // φερε τα δεδομένα απο τον σερβερ
      getData({ variables: { ...newQueryParams } });

      // Ενημέρωσε το State του Route
      //navigate(location.pathname, {
      //   replace: true,
      //   state: {
      //     filterValues: values,
      //     pageIndex: 0,
      //   },
      // });
    },
    [getData, queryParams],
  );

  function useExcelGetQuery() {
    return useLazyQuery(gqlGetYearlyContributionsPerContributor);
    //return [null, { loading: null, data: null }];
  }
  const handleExport = useCallback(async () => {
    const filename = queryParams.contributorId
      ? `Ετήσιες Εισφορές ${queryParams.year} ${queryParams.contributorId}`
      : `Ετήσιες Εισφορές ${queryParams.year}`;
    await exportXlsModal({
      apolloClient: client,
      tableName: undefined,
      getLazyQuery: useExcelGetQuery,
      filterVariables: queryParams,
      exportFileName: filename,
      columnsDef: [
        { fieldName: 'contributorName', fieldTitle: 'Υπόχρεος', fieldType: 'TEXT' },
        { fieldName: 'vatNumber', fieldTitle: 'Α.Φ.Μ.', fieldType: 'TEXT' },
        { fieldName: 'qty1', fieldTitle: 'Ποσότητα 1 (ΤΜΧ)', fieldType: 'FLOAT' },
        { fieldName: 'qty2', fieldTitle: 'Ποσότητα 2 (Kg)', fieldType: 'FLOAT' },
        { fieldName: 'price', fieldTitle: 'Εισφορά σε ευρώ', fieldType: 'FLOAT' },
      ],
    });
  }, [queryParams]);

  const columns = useMemo(
    () => [
      {
        accessor: 'contributorName',
        Header: 'Υπόχρεος',
        Cell: CellLeft,
      },
      {
        accessor: 'vatNumber',
        Header: 'Α.Φ.Μ.',
        Cell: CellLeft,
      },
      {
        accessor: 'qty1',
        Header: 'Ποσότητα 1 (ΤΜΧ)',
        Cell: ({ row }) => (
          <div className="text-center">
            {formatFloat(row.original.qty1, 2)} {row.original.units1}
          </div>
        ),
        Footer: () => {
          return <div className="text-center">{formatFloat(footerValues.qty1, 2)}</div>;
        },
      },
      {
        accessor: 'qty2',
        Header: 'Ποσότητα 2 (Kg)',
        Cell: ({ row }) => (
          <div className="text-center">
            {formatFloat(row.original.qty2, 2)} {row.original.units2}
          </div>
        ),
        Footer: () => {
          return <div className="text-center">{formatFloat(footerValues.qty2, 2)}</div>;
        },
      },
      {
        accessor: 'price',
        Header: 'Εισφορά (€)',
        Cell: CellFloatCustom(5, 2),
        Footer: () => {
          return CellFloatCustom(5, 2)({ cell: { value: footerValues.price } });
        },
      },
    ],
    [footerValues.price, footerValues.qty1, footerValues.qty2],
  );
  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{t('Ετήσια Αθροίσματα Εισφορών ανά Υπόχρεο')}</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        {/* Φίλτρα */}
        <Filters onChange={filtersChanged} initialValues={location?.state?.filterValues ?? {}} />
        {/* TableGrid */}
        <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
          {data ? (
            <TableInMem
              //onRefresh={() => refetch()}
              onExport={handleExport}
              columns={columns}
              data={data}
              hasSearch={false}
              hasFooter={true}
              propPageSize={50}
            />
          ) : (
            <div className="empty-table-container"></div>
          )}
        </LoadingOverlay>
      </Card.Body>
    </Card>
  );
};

/* ----------- Filters ------------------ */
const Filters = ({ onChange, initialValues }) => {
  //
  const { appStore } = useRootStore();
  function handleFiltersSubmit(values, { setSubmitting }) {
    setTimeout(() => {
      setSubmitting(false);
      onChange(values);
    }, 400);
  }
  console.log('filters-init ', initialValues);

  const filters = {
    year: initialValues?.year ?? '',
  };

  //const { values } = useFormikContext();
  const formShape = {
    year: YupStringReq,
  };

  const validationSchema = Yup.object().shape(formShape);
  return (
    <FormFilterCard
      title="Κριτήρια Αναζήτησης"
      onSubmit={handleFiltersSubmit}
      initialValues={{
        year: filters?.year,
      }}
      validationSchema={validationSchema}
    >
      <Row>
        <SelectContributionYear required={true} />
      </Row>
    </FormFilterCard>
  );
};
